// 待宵の花嫁「ヴェールの招待」(veilInvitation・床の帯 kind 'veil') の見た目 — 床から生えて重力で落ちるヴェール (2026-10-23・比較版)。
//
// 作者の原文 (10-23): 「したから生えてきて重力で落ちる感じにしてほしいんだよね。動きとしてはもともとコードだけで作ってた感じにしてほしい」
//   「もともとコードだけで作ってた」= 07-10 の婚礼ヴェールの壁 (render.ts drawVeilWall・絵を使わず菫の柱を3層重ねて描く) と見立てた。
//   次点は 10-23 の布の比較版 (bride_veil_cloth.ts の一枚布)。どちらの動きかは検収ページで聞く。
//   前の答え (答えるページ JsKLdaTP6QT8bqdnwtzLWk) の「両側から挟む」「花びらだけ」はそのまま。津波の4版 (bride_veil_tsunami.ts) は残す。
// 当たりは変えない (src/attacks.ts veilInvitation の2つの帯)。布は帯ごとに1枚、上ほど隙間の側 (もう1つの帯の方) へ寄りかかる。
// 段:
//   ① 予告 (850ms): 床から柱がばらばらに生えてきて、最後に完成の 0.7 くらいの高さまで伸びる。根元の菫の照りが満ちる。
//   ② 当たり 0〜130ms: 一気に伸び切る (少し行き過ぎて戻る)。
//   ③ 当たりの間: 立ったまま上の縁がなびく。
//   ④ 当たりの後: 少し跳ねてから重力で落ちる。柱ごとに少しずつ遅れる。落ちた布は根元に溜まって薄れる。
//      布の濃淡は床に固定する (上の縁が下がるだけで、柄は縮まない)。照りは当たりの終わりですぐ引く。
// 3つ (?brideVeilFx=…・DEV だけ):
//   rise        = 婚礼ヴェールの壁の描き方 (菫の柱3層)。上の縁が重力で落ちる。
//   rise-topple = 同じ描き方で、柱が根元を支点に外側へ倒れる (重力で加速)。
//   rise-cloth  = 布の比較版の描き方 (一枚布・上の縁が波打つ)。上の縁が重力で落ちる。
// 白い線・記号は描かない。描くだけ。
import type { ActiveHit } from './types'
import { hash01 } from './fx_kit'
import { sw as swc } from './purple_swap'

export type VeilRiseKind = 'rise' | 'rise-topple' | 'rise-cloth'
export const VEIL_RISE_KINDS: readonly VeilRiseKind[] = ['rise', 'rise-topple', 'rise-cloth']

function clamp01(v: number): number { return v < 0 ? 0 : v > 1 ? 1 : v }
function smooth01(a: number, b: number, v: number): number {
  const p = clamp01((v - a) / (b - a))
  return p * p * (3 - 2 * p)
}
function easeOut(p: number): number { const q = 1 - clamp01(p); return 1 - q * q * q }
function easeOutBack(p: number): number {
  const x = clamp01(p) - 1
  const c = 1.6
  return 1 + (c + 1) * x * x * x + c * x * x
}

const FULL_H = 246         // 完成の高さ (前の壁と同じ)
const LEAN_PX = 34         // 上の縁が隙間の側へ寄りかかる量 (高さの2乗で効く＝床際はほぼまっすぐ)
const SURGE_MS = 130       // 当たりの 0ms から伸び切るまで
const STAGGER_MS = 140     // 柱ごとの落ちはじめのずれ (最大)
const BLEED = 4            // 帯の左右へはみ出してよい幅 (床際)

// 落ち方。v0=最初に跳ねる速さ (px/s・上へ +)・g=重力 (px/s²)・hold=当たりの後に止まっている時間・total=消えるまで。
type Fall = { v0: number; g: number; holdMs: number; totalMs: number }
const FALL: Record<VeilRiseKind, Fall> = {
  rise: { v0: 150, g: 1500, holdMs: 30, totalMs: 900 },
  'rise-topple': { v0: 0, g: 0, holdMs: 40, totalMs: 820 },
  'rise-cloth': { v0: 150, g: 1500, holdMs: 30, totalMs: 900 },
}
export function veilRiseLingerMs(kind: VeilRiseKind): number { return FALL[kind].totalMs }

type Geom = { id: number; xStart: number; xEnd: number; w: number; cx: number; dir: 1 | -1; seed: number; cols: number }
const dirs = new Map<number, 1 | -1>()
function geomOf(hz: ActiveHit, dir: 1 | -1): Geom {
  const w = hz.xEnd - hz.xStart
  return { id: hz.id, xStart: hz.xStart, xEnd: hz.xEnd, w, cx: (hz.xStart + hz.xEnd) / 2, dir, seed: hz.id * 1.73 + 5.9, cols: Math.max(8, Math.round(w / 22)) }
}

// 柱 i の高さの割合 (0..1+)。予告・当たり・当たりの後の全部をここで決める。ms は当たりの開始から (予告中は負)。
export function veilRiseColumnH(kind: VeilRiseKind, g: Pick<Geom, 'seed' | 'cols'>, i: number, ms: number, telegraphMs: number, activeMs: number): number {
  const r = hash01(g.seed + i * 3.1)
  if (ms < 0) {
    // 生えてくる: 柱ごとに生えはじめがずれる (0〜35%)・最後の 15% で 0.7 へ伸びる
    const p = clamp01((ms + telegraphMs) / telegraphMs)
    const start = 0.05 + 0.3 * r
    return 0.45 * easeOut((p - start) / (0.75 - start * 0.5)) + 0.25 * smooth01(0.85, 1, p)
  }
  const surge = 0.7 + 0.3 * easeOutBack(ms / SURGE_MS)
  const after = ms - activeMs
  if (after <= 0 || kind === 'rise-topple') return surge
  const f = FALL[kind]
  const t = Math.max(0, after - f.holdMs - STAGGER_MS * hash01(g.seed + i * 7.7)) / 1000
  const h = FULL_H + f.v0 * t - 0.5 * f.g * t * t
  return Math.max(0, h / FULL_H)
}

// 倒れる角度 (rise-topple・ラジアン・外側へ +)。重力で加速して約 560ms で横になる。
function toppleAngle(g: Pick<Geom, 'seed'>, i: number, ms: number, activeMs: number): number {
  const after = ms - activeMs - FALL['rise-topple'].holdMs - 110 * hash01(g.seed + i * 7.7)
  if (after <= 0) return 0
  const t = after / 1000
  return Math.min(Math.PI * 0.5, 0.5 * 5.2 * t * t + 0.25 * t)
}

// 根元の菫の照り (弾けない合図)。前の壁と同じ色。clip 外・全幅。
function underGlow(ctx: CanvasRenderingContext2D, g: Geom, floorY: number, a: number, timeSec: number): void {
  if (a <= 0.01) return
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  const throb = 0.75 + 0.25 * Math.sin(timeSec * 3.4)
  const h = 70
  const gr = ctx.createLinearGradient(0, floorY - h, 0, floorY)
  gr.addColorStop(0, `rgba(${swc('unparry', '150,110,204')},0)`)
  gr.addColorStop(1, `rgba(${swc('unparry', '158,116,214')},${(0.6 * a * throb).toFixed(3)})`)
  ctx.fillStyle = gr
  ctx.fillRect(g.xStart, floorY - h, g.w, h)
  ctx.restore()
}

// 前の壁の3層 (深い菫・暗い菫・明るいラベンダー)。topA/botA も前の壁と同じ。
const LAYERS = [
  { hue: '96,60,156', phase: 0.0, sway: 11, topA: 0.14, botA: 0.52 },
  { hue: '68,42,116', phase: 1.4, sway: 8, topA: 0.14, botA: 0.58 },
  { hue: '176,138,230', phase: 2.7, sway: 5, topA: 0.16, botA: 0.40 },
]

type Ctx = CanvasRenderingContext2D

// 柱の形 (前の壁の上の縁 = 低い周波数のなびき＋裂けた不揃い)。
function colTopFrac(i: number, phase: number, t: number): number {
  const billow = 0.15 * (0.5 + 0.5 * Math.sin(t * 0.9 + phase + i * 0.7))
  const tear = 0.09 * (0.5 + 0.5 * Math.sin(i * 2.3 + phase * 1.7))
  return 1 - billow - tear
}

// 柱を描く (婚礼ヴェールの壁の描き方)。hf=高さの割合・ang=倒れる角度。濃淡は床に固定 (縮んで見えない)。
function drawColumns(ctx: Ctx, kind: VeilRiseKind, g: Geom, floorY: number, ms: number, telegraphMs: number, activeMs: number, timeSec: number, alpha: number): void {
  const cw = g.w / g.cols
  const fade = alpha
  if (fade <= 0.01) return
  ctx.save()
  ctx.globalCompositeOperation = 'source-over'
  for (const L of LAYERS) {
    const hue = swc('unparry', L.hue)
    // 濃淡は床から FULL_H まで固定 (落ちても柄は下がらない)
    const gr = ctx.createLinearGradient(0, floorY - FULL_H, 0, floorY)
    gr.addColorStop(0, `rgba(${hue},0)`)
    gr.addColorStop(0.22, `rgba(${hue},${(L.topA * fade).toFixed(3)})`)
    gr.addColorStop(1, `rgba(${hue},${(L.botA * fade).toFixed(3)})`)
    for (let i = 0; i < g.cols; i += 1) {
      const hf = veilRiseColumnH(kind, g, i, ms, telegraphMs, activeMs)
      const colH = FULL_H * colTopFrac(i, L.phase, timeSec) * hf
      if (colH < 1.5) continue
      const foldA = 0.68 + 0.32 * Math.sin(i * 1.3 + L.phase)
      // 落ちている柱ほど大きくなびく (空気を受ける)
      const falling = ms > activeMs ? 1 - hf : 0
      const sway = Math.sin(timeSec * 1.05 + L.phase + i * 0.5) * L.sway * (1 + 1.5 * falling)
      const x0 = Math.max(g.xStart - BLEED, g.xStart + i * cw), x1 = Math.min(g.xEnd + BLEED, g.xStart + (i + 1) * cw + 1)
      const lean = g.dir * LEAN_PX
      const ang = kind === 'rise-topple' ? toppleAngle(g, i, ms, activeMs) * -g.dir : 0
      const pivotX = (x0 + x1) / 2
      // 柱の4隅＋中ほど2点 (下から上へ)。横の寄りかかりと揺れは高さの2乗で効く。
      const pts: Array<[number, number]> = []
      const steps = 4
      for (let s = 0; s <= steps; s += 1) {
        const k = s / steps, y = colH * k, k2 = (y / FULL_H) * (y / FULL_H)
        pts.push([x0 + (lean + sway) * k2, y])
      }
      for (let s = steps; s >= 0; s -= 1) {
        const k = s / steps, y = colH * k, k2 = (y / FULL_H) * (y / FULL_H)
        pts.push([x1 + (lean + sway) * k2, y])
      }
      const c = Math.cos(ang), sn = Math.sin(ang)
      ctx.globalAlpha = foldA
      ctx.fillStyle = gr
      ctx.beginPath()
      for (let p = 0; p < pts.length; p += 1) {
        const [px, py] = pts[p]!
        const dx = px - pivotX
        const X = pivotX + dx * c + py * sn
        const Y = floorY - (-dx * sn + py * c)
        if (p === 0) ctx.moveTo(X, Y); else ctx.lineTo(X, Y)
      }
      ctx.closePath()
      ctx.fill()
    }
  }
  ctx.globalAlpha = 1
  ctx.restore()
}

// 落ちた布が根元に溜まる (高さが下がった柱の分だけ、床際の濃い菫が厚くなる)。
function drawPile(ctx: Ctx, kind: VeilRiseKind, g: Geom, floorY: number, ms: number, telegraphMs: number, activeMs: number, alpha: number): void {
  if (ms <= activeMs || alpha <= 0.01) return
  const cw = g.w / g.cols
  ctx.save()
  for (let i = 0; i < g.cols; i += 1) {
    const fallen = kind === 'rise-topple'
      ? toppleAngle(g, i, ms, activeMs) / (Math.PI * 0.5)
      : 1 - veilRiseColumnH(kind, g, i, ms, telegraphMs, activeMs)
    if (fallen < 0.05) continue
    const h = 6 + 16 * fallen * (0.7 + 0.3 * hash01(g.seed + i))
    const gr = ctx.createLinearGradient(0, floorY - h, 0, floorY)
    gr.addColorStop(0, `rgba(${swc('unparry', '96,60,156')},0)`)
    gr.addColorStop(1, `rgba(${swc('unparry', '96,60,156')},${(0.6 * alpha * fallen).toFixed(3)})`)
    ctx.fillStyle = gr
    ctx.beginPath()
    const x0 = g.xStart + i * cw, x1 = Math.min(g.xEnd, x0 + cw + 1)
    ctx.moveTo(x0, floorY)
    ctx.quadraticCurveTo((x0 + x1) / 2, floorY - h * 1.3, x1, floorY)
    ctx.closePath()
    ctx.fill()
  }
  ctx.restore()
}

// 一枚布 (布の比較版の描き方)。上の縁が波打ち、縁の高さは柱ごとの高さの割合をなめらかにつないだもの。
function drawCloth(ctx: Ctx, g: Geom, floorY: number, ms: number, telegraphMs: number, activeMs: number, timeSec: number, alpha: number): void {
  if (alpha <= 0.01) return
  const step = 5
  const n = Math.max(4, Math.ceil(g.w / step))
  const tx: number[] = [], ty: number[] = []
  let topMin = floorY
  for (let j = 0; j <= n; j += 1) {
    const u = j / n
    const fi = u * (g.cols - 1), i0 = Math.floor(fi), i1 = Math.min(g.cols - 1, i0 + 1), f = fi - i0
    const hf = veilRiseColumnH('rise-cloth', g, i0, ms, telegraphMs, activeMs) * (1 - f) + veilRiseColumnH('rise-cloth', g, i1, ms, telegraphMs, activeMs) * f
    const edge = 0.3 + 0.7 * Math.pow(Math.sin(Math.PI * u), 0.55)   // 帯の端ほど低い (裾の角が垂れる)
    const falling = ms > activeMs ? 1 - hf : 0
    const wave = (14 + 10 * falling) * Math.sin((2 * Math.PI / 150) * (g.xStart + u * g.w) - 3 * timeSec + g.seed) + 4 * Math.sin(0.11 * u * g.w + 4.1 * timeSec)
    const h = Math.max(0, FULL_H * 0.92 * hf * edge + wave * Math.min(1, hf * 3))
    const k2 = (h / FULL_H) * (h / FULL_H)
    const sway = 12 * (1 + falling) * Math.sin(2.4 * timeSec + u * 2.4 + g.seed) * k2
    tx.push(Math.max(g.xStart - BLEED, Math.min(g.xEnd + BLEED, g.xStart + u * g.w)) + (g.dir * LEAN_PX + sway) * k2)
    ty.push(floorY - h)
    topMin = Math.min(topMin, floorY - h)
  }
  if (floorY - topMin < 2) return
  ctx.save()
  ctx.beginPath()
  ctx.moveTo(g.xStart, floorY + 2)
  for (let j = 0; j <= n; j += 1) ctx.lineTo(tx[j]!, ty[j]!)
  ctx.lineTo(g.xEnd, floorY + 2)
  ctx.closePath()
  // 濃淡は床に固定 (前の壁と同じ高さ基準)
  const gr = ctx.createLinearGradient(0, floorY, 0, floorY - FULL_H)
  gr.addColorStop(0, `rgba(${swc('unparry', '150,110,204')},${(0.55 * alpha).toFixed(3)})`)
  gr.addColorStop(0.35, `rgba(${swc('unparry', '170,140,220')},${(0.3 * alpha).toFixed(3)})`)
  gr.addColorStop(0.7, `rgba(214,202,240,${(0.2 * alpha).toFixed(3)})`)
  gr.addColorStop(1, `rgba(214,202,240,${(0.3 * alpha).toFixed(3)})`)
  ctx.fillStyle = gr
  ctx.fill()
  ctx.restore()
}

function alphaAt(kind: VeilRiseKind, ms: number, telegraphMs: number, activeMs: number): number {
  if (ms < 0) return smooth01(0, 0.25, (ms + telegraphMs) / telegraphMs) * (0.75 + 0.25 * clamp01((ms + telegraphMs) / telegraphMs))
  const after = ms - activeMs
  if (after <= 0) return 1
  const total = FALL[kind].totalMs
  if (after > total) return 0
  return 1 - smooth01(total * 0.55, total, after)
}

function drawAt(ctx: Ctx, kind: VeilRiseKind, g: Geom, floorY: number, ms: number, telegraphMs: number, activeMs: number, timeSec: number): void {
  const a = alphaAt(kind, ms, telegraphMs, activeMs)
  // 照り: 予告で満ち、当たりの終わりで 100ms のうちに引く (危険が終わったと分かる)
  const glow = ms < 0 ? 0.3 + 0.6 * clamp01((ms + telegraphMs) / telegraphMs) : (ms < SURGE_MS ? 1.2 : 1) * (1 - smooth01(activeMs - 20, activeMs + 100, ms))
  underGlow(ctx, g, floorY, glow, timeSec)
  if (kind === 'rise-cloth') drawCloth(ctx, g, floorY, ms, telegraphMs, activeMs, timeSec, a)
  else drawColumns(ctx, kind, g, floorY, ms, telegraphMs, activeMs, timeSec, a)
  drawPile(ctx, kind, g, floorY, ms, telegraphMs, activeMs, a)
}

/** 予告 (telegraph)。床から柱が生えてくる。 */
export function drawVeilRiseTelegraph(ctx: Ctx, kind: VeilRiseKind, hz: ActiveHit, timeSec: number, floorY: number, dir: 1 | -1): void {
  dirs.set(hz.id, dir)
  drawAt(ctx, kind, geomOf(hz, dir), floorY, hz.elapsedMs - hz.telegraphMs, hz.telegraphMs, hz.activeMs, timeSec)
}

/** 当たり (active)。 */
export function drawVeilRiseActive(ctx: Ctx, kind: VeilRiseKind, hz: ActiveHit, timeSec: number, floorY: number, dir: 1 | -1): void {
  const ms = hz.elapsedMs - hz.telegraphMs
  const d = dirs.get(hz.id) ?? dir
  dirs.set(hz.id, d)
  const g = geomOf(hz, d)
  if (!traces.has(hz.id)) traces.set(hz.id, { kind, g, startSec: timeSec - ms / 1000, telegraphMs: hz.telegraphMs, activeMs: hz.activeMs })
  drawAt(ctx, kind, g, floorY, ms, hz.telegraphMs, hz.activeMs, timeSec)
}

// ---------------------------------------------------------------- 当たりの後の残り

type Trace = { kind: VeilRiseKind; g: Geom; startSec: number; telegraphMs: number; activeMs: number }
const traces = new Map<number, Trace>()

/** 当たりが消えた後の残り (重力で落ちて、根元に溜まって薄れる)。render.ts の drawHazards の最後で呼ぶ。描くだけ。 */
export function drawVeilRiseLinger(ctx: Ctx, timeSec: number, hits: readonly ActiveHit[], floorY: number): void {
  if (traces.size === 0) return
  for (const [id, tr] of traces) {
    const ms = (timeSec - tr.startSec) * 1000
    if (ms < 0 || ms > tr.activeMs + FALL[tr.kind].totalMs) { traces.delete(id); dirs.delete(id); continue }
    if (hits.some((h) => h.id === id)) continue
    drawAt(ctx, tr.kind, tr.g, floorY, ms, tr.telegraphMs, tr.activeMs, timeSec)
  }
}
